import { faArrowUpArrowDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { Fragment } from 'react';

import { PlayerLiveRatingCard } from '@/modules/players/v2/tabs/rating-history/components/DuprRatingComponents/PlayerLiveRatingCard';
import { ScoreCard } from '@/modules/players/v2/tabs/rating-history/components/DuprRatingComponents/ScoreCard';
import { Match } from '@/modules/players/v2/tabs/rating-history/types';

export enum MatchFormat {
  DOUBLES = 'Doubles',
  SINGLES = 'Singles',
}

export interface GameResultsTableProps {
  matches: Match[];
}

export const GameResultsTable = ({ matches }: GameResultsTableProps) => {
  return (
    <div className='pt-9 md:pt-12'>
      {/* Mobile view (smaller than md screens) */}
      <div className='md:hidden'>
        {matches.map((match, gameIndex) => {
          const isSameModuleTitle =
            gameIndex > 0 && matches[gameIndex - 1]?.moduleTitle === match.moduleTitle;

          return (
            <div key={gameIndex}>
              {!isSameModuleTitle && (
                <div className='rounded-t-lg bg-gray-700 p-3.5 text-white'>
                  <h2 className='text-xl font-bold'>{match.moduleTitle}</h2>
                </div>
              )}
              <div
                key={gameIndex}
                className={`border-x ${gameIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${gameIndex === matches.length - 1 ? 'overflow-hidden rounded-b-lg' : ''}`}
              >
                <div className='p-4'>
                  <div className='flex items-center justify-between'>
                    <p className='text-base font-semibold'>
                      {formatDateTime(match.moduleDate, { localTimezone: true })}
                    </p>
                    <p className='text-sm text-gray-500'>{match.formatTitle}</p>
                  </div>
                </div>
                <hr className='border-gray-200' />

                {match.teamOnePlayerOne ? (
                  <Fragment>
                    <div className='px-6 py-4'>
                      <PlayerLiveRatingCard
                        urlImage={match.teamOnePlayerOne.playerImage}
                        name={`${match.teamOnePlayerOne.firstName} ${match.teamOnePlayerOne.lastName}`}
                        age={match.teamOnePlayerOne.age}
                        gender={match.teamOnePlayerOne.gender}
                        location={[
                          match.teamOnePlayerOne.city,
                          match.teamOnePlayerOne.stateAbbreviation || match.teamOnePlayerOne.state,
                          match.teamOnePlayerOne.countryAbbreviation ||
                            match.teamOnePlayerOne.country,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                        firstMetric={
                          match.formatTitle === MatchFormat.SINGLES
                            ? match.teamOnePlayerOne.previousDuprSinglesRating
                            : match.teamOnePlayerOne.previousDuprDoublesRating
                        }
                        secondMetric={
                          match.formatTitle === MatchFormat.SINGLES
                            ? match.teamOnePlayerOne.currentDuprSinglesRating -
                              match.teamOnePlayerOne.previousDuprSinglesRating
                            : match.teamOnePlayerOne.currentDuprDoublesRating -
                              match.teamOnePlayerOne.previousDuprDoublesRating
                        }
                        thirdMetric={
                          match.formatTitle === MatchFormat.SINGLES
                            ? match.teamOnePlayerOne.currentDuprSinglesRating
                            : match.teamOnePlayerOne.currentDuprDoublesRating
                        }
                      />
                    </div>
                    <hr className='border-gray-200' />
                  </Fragment>
                ) : null}

                {match.teamOnePlayerTwo ? (
                  <Fragment>
                    <div className='px-6 py-4'>
                      <PlayerLiveRatingCard
                        urlImage={match.teamOnePlayerTwo.playerImage}
                        name={`${match.teamOnePlayerTwo.firstName} ${match.teamOnePlayerTwo.lastName}`}
                        age={match.teamOnePlayerTwo.age}
                        gender={match.teamOnePlayerTwo.gender}
                        location={[
                          match.teamOnePlayerTwo.city,
                          match.teamOnePlayerTwo.stateAbbreviation || match.teamOnePlayerTwo.state,
                          match.teamOnePlayerTwo.countryAbbreviation ||
                            match.teamOnePlayerTwo.country,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                        firstMetric={
                          match.formatTitle === MatchFormat.SINGLES
                            ? match.teamOnePlayerTwo.previousDuprSinglesRating
                            : match.teamOnePlayerTwo.previousDuprDoublesRating
                        }
                        secondMetric={
                          match.formatTitle === MatchFormat.SINGLES
                            ? match.teamOnePlayerTwo.currentDuprSinglesRating -
                              match.teamOnePlayerTwo.previousDuprSinglesRating
                            : match.teamOnePlayerTwo.currentDuprDoublesRating -
                              match.teamOnePlayerTwo.previousDuprDoublesRating
                        }
                        thirdMetric={
                          match.formatTitle === MatchFormat.SINGLES
                            ? match.teamOnePlayerTwo.currentDuprSinglesRating
                            : match.teamOnePlayerTwo.currentDuprDoublesRating
                        }
                      />
                    </div>
                    <hr className='border-gray-200' />
                  </Fragment>
                ) : null}

                <div className={`px-4 py-2 ${gameIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                  <ScoreCard
                    teamOneScores={match.teamOne}
                    teamTwoScores={match.teamTwo}
                    winner={match.winner}
                  />
                </div>
                <hr className='border-gray-200' />

                {match.teamTwoPlayerOne ? (
                  <Fragment>
                    <div className='px-6 py-4'>
                      <PlayerLiveRatingCard
                        urlImage={match.teamTwoPlayerOne.playerImage}
                        name={`${match.teamTwoPlayerOne.firstName} ${match.teamTwoPlayerOne.lastName}`}
                        age={match.teamTwoPlayerOne.age}
                        gender={match.teamTwoPlayerOne.gender}
                        location={[
                          match.teamTwoPlayerOne.city,
                          match.teamTwoPlayerOne.stateAbbreviation || match.teamTwoPlayerOne.state,
                          match.teamTwoPlayerOne.countryAbbreviation ||
                            match.teamTwoPlayerOne.country,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                        firstMetric={
                          match.formatTitle === MatchFormat.SINGLES
                            ? match.teamTwoPlayerOne.previousDuprSinglesRating
                            : match.teamTwoPlayerOne.previousDuprDoublesRating
                        }
                        secondMetric={
                          match.formatTitle === MatchFormat.SINGLES
                            ? match.teamTwoPlayerOne.currentDuprSinglesRating -
                              match.teamTwoPlayerOne.previousDuprSinglesRating
                            : match.teamTwoPlayerOne.currentDuprDoublesRating -
                              match.teamTwoPlayerOne.previousDuprDoublesRating
                        }
                        thirdMetric={
                          match.formatTitle === MatchFormat.SINGLES
                            ? match.teamTwoPlayerOne.currentDuprSinglesRating
                            : match.teamTwoPlayerOne.currentDuprDoublesRating
                        }
                      />
                    </div>
                    <hr className='border-gray-200' />
                  </Fragment>
                ) : null}

                {match.teamTwoPlayerTwo ? (
                  <Fragment>
                    <div className='px-6 py-4'>
                      <PlayerLiveRatingCard
                        urlImage={match.teamTwoPlayerTwo.playerImage}
                        name={`${match.teamTwoPlayerTwo.firstName} ${match.teamTwoPlayerTwo.lastName}`}
                        age={match.teamTwoPlayerTwo.age}
                        gender={match.teamTwoPlayerTwo.gender}
                        location={[
                          match.teamTwoPlayerTwo.city,
                          match.teamTwoPlayerTwo.stateAbbreviation || match.teamTwoPlayerTwo.state,
                          match.teamTwoPlayerTwo.countryAbbreviation ||
                            match.teamTwoPlayerTwo.country,
                        ]
                          .filter(Boolean)
                          .join(', ')}
                        firstMetric={
                          match.formatTitle === MatchFormat.SINGLES
                            ? match.teamTwoPlayerTwo.previousDuprSinglesRating
                            : match.teamTwoPlayerTwo.previousDuprDoublesRating
                        }
                        secondMetric={
                          match.formatTitle === MatchFormat.SINGLES
                            ? match.teamTwoPlayerTwo.currentDuprSinglesRating -
                              match.teamTwoPlayerTwo.previousDuprSinglesRating
                            : match.teamTwoPlayerTwo.currentDuprDoublesRating -
                              match.teamTwoPlayerTwo.previousDuprDoublesRating
                        }
                        thirdMetric={
                          match.formatTitle === MatchFormat.SINGLES
                            ? match.teamTwoPlayerTwo.currentDuprSinglesRating
                            : match.teamTwoPlayerTwo.currentDuprDoublesRating
                        }
                      />
                    </div>
                    <hr className='border-gray-200' />
                  </Fragment>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Desktop view (md and larger screens) */}
      <div className='hidden md:block'>
        <div className='overflow-x-auto'>
          <div className='inline-block min-w-full overflow-hidden rounded-lg border-x border-gray-200'>
            <table className='w-full table-fixed border-collapse'>
              {/* Fixed column widths so layout matches the WPR rating-history table. */}
              <colgroup>
                <col style={{ width: '160px' }} />
                <col />
                <col style={{ width: '140px' }} />
                <col />
              </colgroup>
              <thead>
                <tr className='bg-gray-900 text-left text-base font-semibold text-white'>
                  <th className='whitespace-nowrap p-3 pl-4'>
                    Date <FontAwesomeIcon icon={faArrowUpArrowDown} size='sm' />
                  </th>
                  <th className='whitespace-nowrap p-3 pl-4'>Team 1</th>
                  <th className='whitespace-nowrap p-3 pl-4'>Score</th>
                  <th className='whitespace-nowrap p-3 pl-4'>Team 2</th>
                </tr>
              </thead>
              <tbody>
                {matches.map((match, gameIndex) => {
                  const isSameModuleTitle =
                    gameIndex > 0 && matches[gameIndex - 1]?.moduleTitle === match.moduleTitle;

                  return (
                    <Fragment key={gameIndex}>
                      {!isSameModuleTitle && (
                        <tr className='bg-gray-700'>
                          <th colSpan={4} className='px-4 py-3 pr-8'>
                            <div className='flex justify-between text-base font-semibold text-white'>
                              <span>{match.moduleTitle}</span>
                            </div>
                          </th>
                        </tr>
                      )}
                      <tr className={gameIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className='align-center border-r border-gray-200 p-4 text-center text-gray-800'>
                          <p className='text-base font-semibold'>
                            {formatDateTime(match.moduleDate, { localTimezone: true })}
                          </p>
                          <p className='text-sm text-gray-500'>{match.formatTitle}</p>
                        </td>
                        <td className='border-r border-gray-200'>
                          <div className='flex flex-col items-center'>
                            {match.teamOnePlayerOne ? (
                              <div className='w-full px-6 py-5'>
                                <PlayerLiveRatingCard
                                  urlImage={match.teamOnePlayerOne.playerImage}
                                  name={`${match.teamOnePlayerOne.firstName} ${match.teamOnePlayerOne.lastName}`}
                                  age={match.teamOnePlayerOne.age}
                                  gender={match.teamOnePlayerOne.gender}
                                  location={[
                                    match.teamOnePlayerOne.city,
                                    match.teamOnePlayerOne.stateAbbreviation ||
                                      match.teamOnePlayerOne.state,
                                    match.teamOnePlayerOne.countryAbbreviation ||
                                      match.teamOnePlayerOne.country,
                                  ]
                                    .filter(Boolean)
                                    .join(', ')}
                                  firstMetric={
                                    match.formatTitle === MatchFormat.SINGLES
                                      ? match.teamOnePlayerOne.previousDuprSinglesRating
                                      : match.teamOnePlayerOne.previousDuprDoublesRating
                                  }
                                  secondMetric={
                                    match.formatTitle === MatchFormat.SINGLES
                                      ? match.teamOnePlayerOne.currentDuprSinglesRating -
                                        match.teamOnePlayerOne.previousDuprSinglesRating
                                      : match.teamOnePlayerOne.currentDuprDoublesRating -
                                        match.teamOnePlayerOne.previousDuprDoublesRating
                                  }
                                  thirdMetric={
                                    match.formatTitle === MatchFormat.SINGLES
                                      ? match.teamOnePlayerOne.currentDuprSinglesRating
                                      : match.teamOnePlayerOne.currentDuprDoublesRating
                                  }
                                />
                              </div>
                            ) : null}
                            <hr className='w-full border-gray-200' />
                            {match.teamOnePlayerTwo ? (
                              <div className='w-full px-6 py-5'>
                                <PlayerLiveRatingCard
                                  urlImage={match.teamOnePlayerTwo.playerImage}
                                  name={`${match.teamOnePlayerTwo.firstName} ${match.teamOnePlayerTwo.lastName}`}
                                  age={match.teamOnePlayerTwo.age}
                                  gender={match.teamOnePlayerTwo.gender}
                                  location={[
                                    match.teamOnePlayerTwo.city,
                                    match.teamOnePlayerTwo.stateAbbreviation ||
                                      match.teamOnePlayerTwo.state,
                                    match.teamOnePlayerTwo.countryAbbreviation ||
                                      match.teamOnePlayerTwo.country,
                                  ]
                                    .filter(Boolean)
                                    .join(', ')}
                                  firstMetric={
                                    match.formatTitle === MatchFormat.SINGLES
                                      ? match.teamOnePlayerTwo.previousDuprSinglesRating
                                      : match.teamOnePlayerTwo.previousDuprDoublesRating
                                  }
                                  secondMetric={
                                    match.formatTitle === MatchFormat.SINGLES
                                      ? match.teamOnePlayerTwo.currentDuprSinglesRating -
                                        match.teamOnePlayerTwo.previousDuprSinglesRating
                                      : match.teamOnePlayerTwo.currentDuprDoublesRating -
                                        match.teamOnePlayerTwo.previousDuprDoublesRating
                                  }
                                  thirdMetric={
                                    match.formatTitle === MatchFormat.SINGLES
                                      ? match.teamOnePlayerTwo.currentDuprSinglesRating
                                      : match.teamOnePlayerTwo.currentDuprDoublesRating
                                  }
                                />
                              </div>
                            ) : null}
                          </div>
                        </td>
                        <td className='border-r border-gray-200 p-4'>
                          <ScoreCard
                            teamOneScores={match.teamOne}
                            teamTwoScores={match.teamTwo}
                            winner={match.winner}
                          />
                        </td>
                        <td>
                          <div className='flex flex-col items-center'>
                            {match.teamTwoPlayerOne ? (
                              <div className='w-full px-6 py-5'>
                                <PlayerLiveRatingCard
                                  urlImage={match.teamTwoPlayerOne.playerImage}
                                  name={`${match.teamTwoPlayerOne.firstName} ${match.teamTwoPlayerOne.lastName}`}
                                  age={match.teamTwoPlayerOne.age}
                                  gender={match.teamTwoPlayerOne.gender}
                                  location={[
                                    match.teamTwoPlayerOne.city,
                                    match.teamTwoPlayerOne.stateAbbreviation ||
                                      match.teamTwoPlayerOne.state,
                                    match.teamTwoPlayerOne.countryAbbreviation ||
                                      match.teamTwoPlayerOne.country,
                                  ]
                                    .filter(Boolean)
                                    .join(', ')}
                                  firstMetric={
                                    match.formatTitle === MatchFormat.SINGLES
                                      ? match.teamTwoPlayerOne.previousDuprSinglesRating
                                      : match.teamTwoPlayerOne.previousDuprDoublesRating
                                  }
                                  secondMetric={
                                    match.formatTitle === MatchFormat.SINGLES
                                      ? match.teamTwoPlayerOne.currentDuprSinglesRating -
                                        match.teamTwoPlayerOne.previousDuprSinglesRating
                                      : match.teamTwoPlayerOne.currentDuprDoublesRating -
                                        match.teamTwoPlayerOne.previousDuprDoublesRating
                                  }
                                  thirdMetric={
                                    match.formatTitle === MatchFormat.SINGLES
                                      ? match.teamTwoPlayerOne.currentDuprSinglesRating
                                      : match.teamTwoPlayerOne.currentDuprDoublesRating
                                  }
                                />
                              </div>
                            ) : null}
                            <hr className='w-full border-gray-200' />
                            {match.teamTwoPlayerTwo ? (
                              <div className='w-full px-6 py-5'>
                                <PlayerLiveRatingCard
                                  urlImage={match.teamTwoPlayerTwo.playerImage}
                                  name={`${match.teamTwoPlayerTwo.firstName} ${match.teamTwoPlayerTwo.lastName}`}
                                  age={match.teamTwoPlayerTwo.age}
                                  gender={match.teamTwoPlayerTwo.gender}
                                  location={[
                                    match.teamTwoPlayerTwo.city,
                                    match.teamTwoPlayerTwo.stateAbbreviation ||
                                      match.teamTwoPlayerTwo.state,
                                    match.teamTwoPlayerTwo.countryAbbreviation ||
                                      match.teamTwoPlayerTwo.country,
                                  ]
                                    .filter(Boolean)
                                    .join(', ')}
                                  firstMetric={
                                    match.formatTitle === MatchFormat.SINGLES
                                      ? match.teamTwoPlayerTwo.previousDuprSinglesRating
                                      : match.teamTwoPlayerTwo.previousDuprDoublesRating
                                  }
                                  secondMetric={
                                    match.formatTitle === MatchFormat.SINGLES
                                      ? match.teamTwoPlayerTwo.currentDuprSinglesRating -
                                        match.teamTwoPlayerTwo.previousDuprSinglesRating
                                      : match.teamTwoPlayerTwo.currentDuprDoublesRating -
                                        match.teamTwoPlayerTwo.previousDuprDoublesRating
                                  }
                                  thirdMetric={
                                    match.formatTitle === MatchFormat.SINGLES
                                      ? match.teamTwoPlayerTwo.currentDuprSinglesRating
                                      : match.teamTwoPlayerTwo.currentDuprDoublesRating
                                  }
                                />
                              </div>
                            ) : null}
                          </div>
                        </td>
                      </tr>

                      <tr>
                        <td colSpan={4} className='p-0'>
                          <hr className='border-gray-200' />
                        </td>
                      </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
